export type BlogLocale = "hr" | "en";

export type BlogPostStatus = "draft" | "published";

export type BlogFrontmatter = {
  title: string;
  description: string;
  locale: BlogLocale;
  slug: string;
  translationKey: string;
  date: string;
  updated: string;
  status: BlogPostStatus;
  tags: string[];
  heroPlaceholder: string;
};

export type MarkdownBlock =
  | { type: "heading"; level: 2 | 3; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; ordered: boolean; items: string[] }
  | { type: "quote"; text: string }
  | { type: "placeholder"; alt: string; id: string };

export type BlogPost = BlogFrontmatter & {
  body: string;
  blocks: MarkdownBlock[];
  excerpt: string;
  readingTimeMinutes: number;
  path: string;
};

export const BLOG_LOCALES: BlogLocale[] = ["hr", "en"];

const BLOG_BASE_URL = "https://thenea.app";

const modules = import.meta.glob("../../content/blog/**/*.md", {
  eager: true,
  import: "default",
  query: "?raw",
}) as Record<string, string>;

const stripQuotes = (value: string) => value.replace(/^['"]|['"]$/g, "");

const parseArray = (value: string) => value
  .replace(/^\[/, "")
  .replace(/\]$/, "")
  .split(",")
  .map((item) => stripQuotes(item.trim()))
  .filter(Boolean);

const parseFrontmatter = (source: string): { frontmatter: BlogFrontmatter; body: string } => {
  const match = source.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) throw new Error("Blog post is missing frontmatter.");

  const raw: Record<string, string | string[]> = {};
  match[1].split(/\r?\n/).forEach((line) => {
    const separator = line.indexOf(":");
    if (separator === -1) return;
    const key = line.slice(0, separator).trim();
    const value = line.slice(separator + 1).trim();
    raw[key] = value.startsWith("[") ? parseArray(value) : stripQuotes(value);
  });

  const locale = raw.locale === "en" ? "en" : "hr";
  const status = raw.status === "draft" ? "draft" : "published";

  return {
    frontmatter: {
      title: String(raw.title || ""),
      description: String(raw.description || ""),
      locale,
      slug: String(raw.slug || ""),
      translationKey: String(raw.translationKey || ""),
      date: String(raw.date || ""),
      updated: String(raw.updated || raw.date || ""),
      status,
      tags: Array.isArray(raw.tags) ? raw.tags : [],
      heroPlaceholder: String(raw.heroPlaceholder || "Blog article screenshot placeholder"),
    },
    body: match[2].trim(),
  };
};

const isSpecialLine = (line: string) => (
  /^#{1,3}\s/.test(line)
  || /^[-*]\s+/.test(line)
  || /^\d+\.\s+/.test(line)
  || /^>\s?/.test(line)
  || /^!\[[^\]]*\]\([^)]*\)/.test(line)
);

export const parseMarkdownBlocks = (markdown: string): MarkdownBlock[] => {
  const lines = markdown.split(/\r?\n/);
  const blocks: MarkdownBlock[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index].trim();

    if (!line) {
      index += 1;
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      if (level > 1) blocks.push({ type: "heading", level: level === 2 ? 2 : 3, text: heading[2] });
      index += 1;
      continue;
    }

    const placeholder = line.match(/^!\[([^\]]*)\]\((placeholder:[^)]+)\)$/);
    if (placeholder) {
      blocks.push({ type: "placeholder", alt: placeholder[1], id: placeholder[2].replace("placeholder:", "") });
      index += 1;
      continue;
    }

    if (/^[-*]\s+/.test(line) || /^\d+\.\s+/.test(line)) {
      const ordered = /^\d+\.\s+/.test(line);
      const items: string[] = [];
      while (index < lines.length) {
        const current = lines[index].trim();
        const match = ordered ? current.match(/^\d+\.\s+(.+)$/) : current.match(/^[-*]\s+(.+)$/);
        if (!match) break;
        items.push(match[1]);
        index += 1;
      }
      blocks.push({ type: "list", ordered, items });
      continue;
    }

    if (/^>\s?/.test(line)) {
      const parts: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index].trim())) {
        parts.push(lines[index].trim().replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push({ type: "quote", text: parts.join(" ") });
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index].trim();
      if (!current || isSpecialLine(current)) break;
      paragraph.push(current);
      index += 1;
    }
    if (paragraph.length > 0) blocks.push({ type: "paragraph", text: paragraph.join(" ") });
  }

  return blocks;
};

const getExcerpt = (blocks: MarkdownBlock[]) => {
  const paragraph = blocks.find((block): block is Extract<MarkdownBlock, { type: "paragraph" }> => block.type === "paragraph");
  return paragraph?.text ?? "";
};

const getReadingTime = (body: string) => {
  const words = body.replace(/[#>*_`()[\]!-]/g, " ").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 220));
};

const toPost = (source: string): BlogPost => {
  const { frontmatter, body } = parseFrontmatter(source);
  const blocks = parseMarkdownBlocks(body);
  return {
    ...frontmatter,
    body,
    blocks,
    excerpt: getExcerpt(blocks),
    readingTimeMinutes: getReadingTime(body),
    path: `/${frontmatter.locale}/blog/${frontmatter.slug}`,
  };
};

const posts = Object.values(modules)
  .map(toPost)
  .filter((post) => post.status === "published" && post.slug && post.title)
  .sort((a, b) => `${b.date}-${b.slug}`.localeCompare(`${a.date}-${a.slug}`));

export const getBlogPosts = (locale?: BlogLocale) => posts.filter((post) => !locale || post.locale === locale);

export const getBlogPost = (locale: BlogLocale, slug: string | undefined) => posts.find(
  (post) => post.locale === locale && post.slug === slug,
);

export const getBlogPostByTranslationKey = (translationKey: string, locale: BlogLocale) => posts.find(
  (post) => post.locale === locale && post.translationKey === translationKey,
);

export const getBlogPostPath = (locale: BlogLocale, slug: string) => `/${locale}/blog/${slug}`;

export const getBlogIndexPath = (locale: BlogLocale) => `/${locale}/blog`;

export const getCanonicalUrl = (path: string) => `${BLOG_BASE_URL}${path}`;

export const getBlogLocaleSwitchPath = (pathname: string, targetLocale: BlogLocale) => {
  const match = pathname.match(/^\/(hr|en)\/blog(?:\/([^/]+))?\/?$/);
  if (!match) return null;

  const currentLocale = match[1] as BlogLocale;
  const slug = match[2];
  if (!slug) return getBlogIndexPath(targetLocale);

  const currentPost = getBlogPost(currentLocale, slug);
  if (!currentPost) return getBlogIndexPath(targetLocale);

  const translatedPost = getBlogPostByTranslationKey(currentPost.translationKey, targetLocale);
  return translatedPost ? translatedPost.path : getBlogIndexPath(targetLocale);
};

export const formatBlogDate = (date: string, locale: BlogLocale) => new Intl.DateTimeFormat(locale === "hr" ? "hr-HR" : "en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
}).format(new Date(`${date}T00:00:00Z`));
